import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { DatePipe } from '@angular/common';
import { TuiButton } from '@taiga-ui/core';
import {
  SupportKind,
  SupportMessage,
  SupportThread,
  SupportThreadSummary,
} from '../../models/support.models';
import { SupportApiService } from '../../services/support-api.service';
import { supportErrorMessage } from '../../utils/support-error-message';
import { SupportUnreadService } from '../../services/support-unread.service';
import { ScreenshotPickerComponent } from '../screenshot-picker/screenshot-picker.component';

export const SUBJECT_MAX = 150;
export const BODY_MAX = 5000;

const notBlank = (control: AbstractControl): ValidationErrors | null =>
  typeof control.value === 'string' && control.value.trim() === '' ? { required: true } : null;

type PanelView = 'list' | 'new' | 'thread';

@Component({
  selector: 'app-support-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, DatePipe, TuiButton, ScreenshotPickerComponent],
  templateUrl: './support-panel.component.html',
  styleUrl: './support-panel.component.less',
  // Pasted screenshots land in the picker wherever focus is inside the panel.
  host: { '(paste)': 'onPaste($event)' },
})
export class SupportPanelComponent {
  private readonly api = inject(SupportApiService);
  private readonly unread = inject(SupportUnreadService);
  private readonly router = inject(Router);
  private readonly picker = viewChild(ScreenshotPickerComponent);

  readonly subjectMax = SUBJECT_MAX;
  readonly bodyMax = BODY_MAX;
  readonly kinds: { value: SupportKind; label: string }[] = [
    { value: 'bug', label: 'Bug' },
    { value: 'feature', label: 'Feature request' },
    { value: 'question', label: 'Question' },
  ];

  readonly view = signal<PanelView>('list');
  readonly threads = signal<SupportThreadSummary[]>([]);
  readonly thread = signal<SupportThread | null>(null);
  readonly loading = signal(false);
  readonly sending = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly files = signal<File[]>([]);

  readonly newForm = new FormGroup({
    kind: new FormControl<SupportKind>('bug', { nonNullable: true }),
    subject: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, notBlank, Validators.maxLength(SUBJECT_MAX)],
    }),
    body: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, notBlank, Validators.maxLength(BODY_MAX)],
    }),
  });

  readonly replyForm = new FormGroup({
    body: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, notBlank, Validators.maxLength(BODY_MAX)],
    }),
  });

  constructor() {
    this.loadList();
  }

  /** Path without query and fragment, so no tokens or filters leave with the report. */
  get pagePath(): string {
    return this.router.url.split(/[?#]/)[0];
  }

  loadList(): void {
    this.loading.set(true);
    this.api.listThreads().subscribe({
      next: (items) => {
        this.threads.set(items);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set('Could not load your requests.');
        this.loading.set(false);
      },
    });
  }

  showList(): void {
    this.view.set('list');
    this.errorMessage.set(null);
    this.loadList();
  }

  startNew(): void {
    this.newForm.reset({ kind: 'bug', subject: '', body: '' });
    this.files.set([]);
    this.errorMessage.set(null);
    this.view.set('new');
  }

  setKind(kind: SupportKind): void {
    this.newForm.controls.kind.setValue(kind);
  }

  openThread(id: number): void {
    this.errorMessage.set(null);
    this.api.getThread(id).subscribe({
      next: (thread) => {
        this.showThread(thread);
        // Opening marks the thread read on the server.
        this.unread.refresh();
      },
      error: () => this.errorMessage.set('Could not open the request.'),
    });
  }

  submitNew(): void {
    if (this.newForm.invalid || this.sending()) {
      this.newForm.markAllAsTouched();
      return;
    }
    const value = this.newForm.getRawValue();
    this.sending.set(true);
    this.errorMessage.set(null);
    this.api
      .createThread({
        kind: value.kind,
        subject: value.subject.trim(),
        body: value.body.trim(),
        images: this.files(),
        pageUrl: this.pagePath,
        viewport: `${window.innerWidth}x${window.innerHeight}`,
      })
      .subscribe({
        next: (thread) => {
          this.sending.set(false);
          this.showThread(thread);
          this.unread.refresh();
        },
        error: (err: unknown) => {
          this.sending.set(false);
          this.errorMessage.set(supportErrorMessage(err, 'Could not send your request. Please try again.'));
        },
      });
  }

  submitReply(): void {
    const current = this.thread();
    if (!current || this.replyForm.invalid || this.sending()) {
      this.replyForm.markAllAsTouched();
      return;
    }
    this.sending.set(true);
    this.errorMessage.set(null);
    this.api.reply(current.id, this.replyForm.getRawValue().body.trim(), this.files()).subscribe({
      next: () => {
        this.sending.set(false);
        this.replyForm.reset({ body: '' });
        this.files.set([]);
        this.unread.refresh();
        // Re-fetch: a user reply reopens an answered thread.
        this.api.getThread(current.id).subscribe((t) => this.thread.set(t));
      },
      error: (err: unknown) => {
        this.sending.set(false);
        this.errorMessage.set(supportErrorMessage(err, 'Could not send your reply. Please try again.'));
      },
    });
  }

  closeThread(): void {
    const current = this.thread();
    if (!current) return;
    this.api.closeThread(current.id).subscribe({
      next: (summary) => {
        this.thread.set({ ...current, status: summary.status });
        this.unread.refresh();
      },
      error: () => this.errorMessage.set('Could not close the request.'),
    });
  }

  onPaste(event: ClipboardEvent): void {
    this.picker()?.handlePaste(event);
  }

  isStaff(message: SupportMessage): boolean {
    return message.author_kind === 'admin';
  }

  attachmentUrl = (a: SupportMessage['attachments'][number]): string => this.api.attachmentUrl(a);

  private showThread(thread: SupportThread): void {
    this.thread.set(thread);
    this.replyForm.reset({ body: '' });
    this.files.set([]);
    this.view.set('thread');
  }
}
